/**
 * Notification system contracts — shared by the dashboard server (which
 * detects + dispatches) and the web client (which renders the
 * preferences matrix + the bell). Single source of truth so the two
 * sides can never disagree about which categories exist, what gates
 * them, or which pulse queue feeds them.
 *
 * Design: `docs/superpowers/specs/2026-05-20-desktop-app-notifications-design.md`.
 */
import type { PulseQueues } from "./contracts.js";
import {
  type Permission,
  type Section,
  hasPermission,
} from "./permissions.js";

/** The three delivery channels a user can opt into per category. The
 * in-app bell is NOT a channel — it always shows everything the user is
 * permitted to see; these three are the "interruption" surfaces. */
export const NOTIFICATION_CHANNELS = ["email", "desktop", "webpush"] as const;
export type NotificationChannel = (typeof NOTIFICATION_CHANNELS)[number];

/** Every notifiable category. Each maps 1:1 to a pulse signal. */
export const NOTIFICATION_CATEGORY_IDS = [
  "support_new_ticket",
  "support_sla_breach",
  "support_conversation",
  "sales_new_lead",
  "sales_lead_closing",
  "marketing_review",
  "crm_invoice_overdue",
  "server_down",
  "call_incoming",
  "chat_mention",
  "chat_message",
  "call_missed",
] as const;
export type NotificationCategoryId = (typeof NOTIFICATION_CATEGORY_IDS)[number];

/**
 * How a category's events are produced. `"pulse"` (the default) means the
 * periodic dispatcher derives them by diffing a pulse queue; `"event"` means
 * they are fired event-driven at the moment they happen (e.g. a chat @mention
 * at send time) through `app.dispatchNotification`, NOT by the poll loop. The
 * dispatcher skips `"event"` categories so they are never double-produced.
 */
export type NotificationSource = "pulse" | "event";

export type NotificationSeverity = "info" | "warn" | "critical";

export interface NotificationCategory {
  id: NotificationCategoryId;
  label: string;
  /** The nav section this belongs to (used for grouping in the UI). */
  section: Section;
  /** Permission that gates the category. No grant ⇒ the row never
   * renders and the dispatcher never produces an event for it. */
  permission: Permission;
  severity: NotificationSeverity;
  /** Channels enabled by default when the user has no explicit row. */
  defaultChannels: NotificationChannel[];
  /** How events are produced. Defaults to `"pulse"` when absent. */
  source?: NotificationSource;
  /** Which pulse queue (or `systems`) this category reads from. Required for
   * `"pulse"` categories; omitted for `"event"` categories (they have no
   * queue). */
  pulseQueue?: keyof PulseQueues | "systems";
}

/**
 * The category registry. Order here is the display order in the
 * preferences matrix.
 */
export const NOTIFICATION_CATEGORIES: Record<
  NotificationCategoryId,
  NotificationCategory
> = {
  support_new_ticket: {
    id: "support_new_ticket",
    label: "New support tickets",
    section: "support",
    permission: "support:view",
    severity: "info",
    defaultChannels: ["desktop"],
    pulseQueue: "new_tickets",
  },
  support_sla_breach: {
    id: "support_sla_breach",
    label: "SLA breaches",
    section: "support",
    permission: "support:view",
    severity: "warn",
    defaultChannels: ["desktop", "email"],
    pulseQueue: "sla_breaches",
  },
  support_conversation: {
    id: "support_conversation",
    label: "New Otto conversations",
    section: "support",
    permission: "support:view",
    severity: "info",
    // Noisy by nature — off by default; users opt in.
    defaultChannels: [],
    pulseQueue: "recent_conversations",
  },
  sales_new_lead: {
    id: "sales_new_lead",
    label: "New sales leads",
    section: "sales",
    permission: "sales:view",
    severity: "info",
    defaultChannels: ["desktop"],
    pulseQueue: "leads_pending",
  },
  sales_lead_closing: {
    id: "sales_lead_closing",
    label: "Lead deadlines closing",
    section: "sales",
    permission: "sales:view",
    // A closing window is time-critical — warn by default; the dispatcher
    // escalates an individual ≤3-day "last chance" event to `critical`.
    severity: "warn",
    defaultChannels: ["desktop", "email"],
    pulseQueue: "leads_closing_soon",
  },
  marketing_review: {
    id: "marketing_review",
    label: "Marketing drafts to review",
    section: "marketing",
    permission: "marketing:view",
    severity: "info",
    defaultChannels: ["desktop"],
    pulseQueue: "marketing_review",
  },
  crm_invoice_overdue: {
    id: "crm_invoice_overdue",
    label: "Overdue invoices",
    section: "crm",
    // Behind the financial wall, NOT the coarse `crm:view` entry gate. The
    // event body carries the invoice's dollar amount (the pulse row `meta`), so
    // a CRM viewer without financial access — e.g. a marketing_manager — must
    // never receive it. Mirrors the `crm:financials` guard on the Invoices page,
    // the /api/crm `/invoices` proxy, and the dashboard's Invoices-due card.
    permission: "crm:financials",
    severity: "warn",
    defaultChannels: ["desktop", "email"],
    pulseQueue: "invoices_due",
  },
  server_down: {
    id: "server_down",
    label: "Server / system down",
    section: "server",
    permission: "server:view",
    severity: "critical",
    defaultChannels: ["desktop", "webpush", "email"],
    pulseQueue: "systems",
  },
  call_incoming: {
    id: "call_incoming",
    label: "Incoming calls",
    // Calls live in the chat section — 1:1 calling is a chat feature.
    section: "chat",
    permission: "chat:view",
    severity: "info",
    // Calls are time-critical interrupts: desktop popup + web-push by default.
    defaultChannels: ["desktop", "webpush"],
    // Event-driven (delivered client-side off the `call_invite` realtime frame),
    // not queue-backed — so it has no pulse queue and the poll loop skips it.
    // (This replaces the earlier `pulseQueue: "systems"` sentinel + the
    // `rowsForCategory` special-case the `source` field made unnecessary.)
    source: "event",
  },
  chat_mention: {
    id: "chat_mention",
    label: "Chat mentions",
    section: "chat",
    // Every built-in role carries chat:view, so the mention category is
    // universally available — gated like the chat surface itself.
    permission: "chat:view",
    // "Someone is trying to reach you" — louder than an ambient new message.
    severity: "warn",
    defaultChannels: ["desktop", "email"],
    // Fired event-driven at send time (app.dispatchNotification), NOT polled —
    // so it has no pulse queue and the dispatch loop skips it.
    source: "event",
  },
  chat_message: {
    id: "chat_message",
    label: "New direct messages",
    section: "chat",
    // Same universal reach as chat_mention — every built-in role holds chat:view.
    permission: "chat:view",
    // "You were sent a message while away" — the same register as a mention,
    // but only fired when the recipient is OFFLINE (a connected user gets live
    // delivery + the focus-gated in-app toast instead, so no email/bell noise).
    severity: "warn",
    // Email-only by default: a disconnected user can't receive a desktop / web
    // push, and the bell + live chat cover them when they reconnect. Users can
    // still opt those channels on. Fired event-driven (offline DMs only), so it
    // has no pulse queue and the poll loop skips it.
    defaultChannels: ["email"],
    source: "event",
  },
  call_missed: {
    id: "call_missed",
    label: "Missed calls",
    section: "chat",
    permission: "chat:view",
    // Fired server-side when you ring a teammate who holds no live connection
    // (the 409 CALL_OFFLINE case) — the call can't ring through, so they'd
    // otherwise never know. Distinct from `call_incoming` (the live-ring
    // interrupt delivered client-side off the call_invite frame).
    severity: "warn",
    // Email-only by default, same rationale as chat_message (the callee is
    // offline — a live push can't reach them).
    defaultChannels: ["email"],
    source: "event",
  },
};

/** A single dispatched notification (wire shape on the SSE stream and in
 * the bell list). */
export interface NotificationEvent {
  /** Stable id — `${category}:${source_id}` so re-delivery dedupes. */
  id: string;
  category: NotificationCategoryId;
  severity: NotificationSeverity;
  title: string;
  body: string;
  /** In-app deep link (e.g. `/support/tickets/5012`). */
  link: string | null;
  /** The underlying pulse row id this was derived from. */
  source_id: string;
  created_at: string;
  /** When true the client plays an audible chime on arrival (gated by the same
   * mute / desktop-pref as the popup). Set for high-signal events like a chat
   * @mention. Optional — absent ⇒ silent (every existing category). */
  sound?: boolean;
}

/**
 * Build the `call_missed` notification fired at a teammate you couldn't reach by
 * calling — shared by BOTH offline paths so the wire shape can't drift:
 *   - the callee held no live connection (the `409 CALL_OFFLINE` ring routes), and
 *   - a 1:1 ring that timed out with no answer (`reason: "missed"`).
 * `createdAt` is passed in (not read from the clock) so the id is deterministic
 * for tests; each attempt stamps a fresh timestamp so it's a distinct bell row,
 * while the per-(user, category) email throttle coalesces repeats. `body`
 * defaults to the no-answer wording; the offline path overrides it.
 */
export function buildMissedCallNotification(args: {
  callerId: number;
  callerName: string;
  calleeId: number;
  createdAt: string;
  body?: string;
}): NotificationEvent {
  const name = args.callerName?.trim() || "A teammate";
  return {
    id: `call_missed:${args.callerId}:${args.calleeId}:${args.createdAt}`,
    category: "call_missed",
    severity: "warn",
    title: `${name} tried to call you`,
    body: args.body ?? "You have a missed call.",
    link: `/chat?dm=${args.callerId}`,
    source_id: String(args.calleeId),
    created_at: args.createdAt,
  };
}

/** Persisted bell record = event plus its read state. */
export interface NotificationRecord extends NotificationEvent {
  read_at: string | null;
}

/** One cell of the preferences matrix. */
export interface NotificationPreference {
  category: NotificationCategoryId;
  channel: NotificationChannel;
  enabled: boolean;
}

/** Browser Push subscription as POSTed from the service worker. */
export interface PushSubscriptionInput {
  endpoint: string;
  keys: {
    p256dh: string;
    auth: string;
  };
}

/** Wire payloads for the notification routes. */
export interface NotificationPreferencesResponse {
  /** Only the categories the caller is permitted to see. */
  categories: NotificationCategory[];
  /** Effective per-cell state (defaults merged with stored overrides),
   * limited to the permitted categories. */
  preferences: NotificationPreference[];
  /** Global mute — suppresses all interruption channels at once. */
  muted: boolean;
}

export interface UpdateNotificationPreferencesRequest {
  preferences: NotificationPreference[];
  muted?: boolean;
}

export interface NotificationFeedResponse {
  items: NotificationRecord[];
  unread: number;
  page: number;
  per_page: number;
  total: number;
}

/**
 * Filter the full category list down to the ones a permission set can
 * see. Used by both the prefs route (server gate) and the prefs UI
 * (render gate).
 */
export function categoriesForPermissions(
  granted: readonly Permission[] | undefined,
): NotificationCategory[] {
  return NOTIFICATION_CATEGORY_IDS.map((id) => NOTIFICATION_CATEGORIES[id]).filter(
    (c) => hasPermission(granted, c.permission),
  );
}

/** The default preference rows for a permitted category set — used as the
 * baseline the dispatcher and the UI both reason over. */
export function defaultPreferences(
  categories: readonly NotificationCategory[],
): NotificationPreference[] {
  const rows: NotificationPreference[] = [];
  for (const c of categories) {
    for (const channel of NOTIFICATION_CHANNELS) {
      rows.push({
        category: c.id,
        channel,
        enabled: c.defaultChannels.includes(channel),
      });
    }
  }
  return rows;
}
